import { ListBoxContainer, PrimaryButton, ReactIcons, Switch } from "@kalyzee/kalyzee-ui"
import { MeetingContainerAPI, mediaSessionIsHidden, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import MeetingSessionMediaPlayer from "../player/MeetingSessionMediaPlayer";
import { VideoPlayer } from "../player/VideoPlayer";

import workBoxStyles from "./WorkBox.module.css";
import { useMemo } from "react";
import { useBoardMedia } from "../../hooks/board";
import { LocalOrRemoteMedia } from "../../hooks/ovenmedia";
import { Meeting, MeetingSessionMediaStatus } from "@kalyzee/kast-web-sdk/websocket";
import { MasterPlayerWorkBox } from "./MasterPlayerWorkBox";

import styles from "./BoardInputsWorbox.module.css";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import clsx from "clsx";
import { toastError } from "../../helpers/toast";

export interface InputsWorkBoxProps {
  meetingAPI: MeetingContainerAPI;
  onClose?: () => void;
}

export const BoardInputsWorkBox = ({
  meetingAPI,
  onClose,
}: InputsWorkBoxProps) => {
  const [meeting] = useMeetingSelector(meetingAPI, "meeting");
  const [sessions] = useMeetingSelector(meetingAPI, "sessions");
  const [ownSession] = useMeetingSelector(meetingAPI, "session");

  const dispatch = useAppStoreDispatch();
  const meetingOptions = useAppStoreSelector((state) => state.meetingOptions);

  const inputs = useBoardMedia();

  const inputItems = useMemo(() => {
    if (!meeting || !inputs) return [];
    const options = meetingOptions[meeting.id] ?? {};

    const onSelectInputItem = (sessionId: string) => {
      const selectedSessions = options.disabledVideoSessionIds ?? [];
      const index = selectedSessions.indexOf(sessionId);
      if (index === -1) {
        dispatch("updateMeetingOptions", {
          meetingId: meeting.id,
          options: {
            disabledVideoSessionIds: [...selectedSessions, sessionId],
          },
        });
      } else {
        dispatch("updateMeetingOptions", {
          meetingId: meeting.id,
          options: {
            disabledVideoSessionIds: selectedSessions.filter((s) => s !== sessionId),
          },
        });
      }
    };

    /**
     * Own streams are hidden at the source instead of locally: flipping their
     * media status to `hidden` keeps the session alive but mutes the video for
     * everyone. Other people's streams can only be hidden on this client.
     */
    const onToggleOwnInput = async (sessionId: string, mediaSessionId: string, hidden: boolean) => {
      const result = await meetingAPI.updater.medias.update(sessionId, mediaSessionId, {
        status: hidden ? MeetingSessionMediaStatus.HIDDEN : MeetingSessionMediaStatus.ENABLED,
      });
      // The store only takes the server's answer, so a rejected update leaves
      // the switch where it was — it just needs to be said out loud.
      if (!result || result.error || !result.response) {
        toastError("Impossible de modifier l'affichage de votre flux.");
      }
    };

    const data = inputs.map((input) => {
      const mediaSessionId = input.mediaSession.id;
      const isOwn = !!ownSession && input.session.id === ownSession.id;
      const hidden = mediaSessionIsHidden(input.mediaSession);
      const isSelected = isOwn
        ? !hidden
        : !options?.disabledVideoSessionIds?.includes(mediaSessionId);
      return {
        input,
        isOwn,
        hidden,
        selected: isSelected,
        onSelect: () => (isOwn
          ? onToggleOwnInput(input.session.id, mediaSessionId, isSelected)
          : onSelectInputItem(mediaSessionId)),
      };
    });

    return data;
  }, [meeting, meetingOptions, inputs, ownSession, meetingAPI, dispatch]);
  
  const renderLocalOrRemoteInput = (
    meeting: Meeting,
    input: LocalOrRemoteMedia,
    selected: boolean,
  ) => {
    return (
      input.type === "remote" ? (
        <MeetingSessionMediaPlayer
          meeting={meeting}
          sessions={sessions ?? []}
          sessionId={input.session.id}
          mediaSessionId={input.mediaSession.id}
          ovenMediaSession={input.videoSession}
          muted={true}
          style={{
            overflow: "hidden",
            width: "100%",
            height: "100%",
          }}
          videoStyle={{
            objectFit: "cover",
          }}
        />
      ) : (
        <VideoPlayer
          videoStyle={{ objectFit: "cover", overflow: "hidden" }}
          src={input.stream.getMediaStream()}
          muted
          meeting={meeting}
          session={input.session}
          mediaSession={input.mediaSession}
          local
        />
      )
    );
  }

  const renderItems = () => {
    if (!meeting) return [];
    return inputItems.map((inputItem) => {
      const input = inputItem.input;
      return (
        <MasterPlayerWorkBox
          meetingAPI={meetingAPI}
          session={input.session}
          mediaSession={input.mediaSession}
          localStream={input.type === "local"}
          extraHeaderContent={
            <Switch
              value={inputItem.selected}
              switchNativeProps={{ disabled: true }}
              className={styles.switch}
            />
          }
          className={clsx({
            [styles.inputContainer]: true,
            [styles.selected]: inputItem.selected,
            [styles.ownVisible]: inputItem.isOwn && inputItem.selected,
            [styles.ownHidden]: inputItem.isOwn && !inputItem.selected,
          })}
          topBarClassName={styles.inputTopBar}
          boxProps={{
            onClick: inputItem.onSelect,
            style: {
              aspectRatio: "3 / 1.25",
            }
          }}
          overlayMode="overlay"
          mediaButtons={false}
        >
          {renderLocalOrRemoteInput(meeting, input, inputItem.selected)}
          {/* Hidden at the source: absent from the grids, so the reason is
              spelled out here — worded from the reader's point of view. */}
          {inputItem.hidden && (
            <span className={styles.hiddenBadge}>
              {inputItem.isOwn ? "Masqué pour tous" : "Masqué par l'émetteur"}
            </span>
          )}
        </MasterPlayerWorkBox>
      );
    });
  }

  return (
    <ListBoxContainer
      style={{ width: "100%", height: "100%" }}
      topBarProps={{
        left: [
          <ReactIcons.ComputerScreen style={{ width: "1.5em", height: "1.5em" }} />,
        ],
        center: [
          <span className={workBoxStyles.label}>Affichage</span>
        ],
        right: onClose ? [
          <PrimaryButton onClick={onClose}>
            <ReactIcons.Close style={{ height: "0.8em" }} />
          </PrimaryButton>
        ] : [],
      }}
      items={renderItems()}
    />
  );
}